/**
 * useOptions Hook
 *
 * 核心职责:
 * 1. 管理 options 数据状态
 * 2. flattenTree - 将树形 options 展平为路径数组(用于搜索)
 * 3. generateFilteredOptions - 根据搜索关键词生成过滤后的选项
 * 4. getFilledFieldNames - 获取完整的字段名映射
 *
 * 设计要点:
 * - 使用 useMemo 缓存 flattenOptions,避免重复计算
 * - 仅在 showSearch=true 时才展平树形结构
 * - 支持自定义字段名映射
 */

import { useMemo, useCallback } from 'react';
import React from 'react';
import Empty from '../../../wui-empty/src';
import type { CascaderOption, FieldNames, ShowSearchType } from '../iCascader';

const keepFilteredValueField = '__KEEP_FILTERED_OPTION_VALUE';

/**
 * FilledFieldNames - label、value、children 必填，其他可选
 */
export type FilledFieldNames = Required<Pick<FieldNames, 'label' | 'value' | 'children'>> & Omit<FieldNames, 'label' | 'value' | 'children'>;

export interface UseOptionsProps {
    options: CascaderOption[];
    showSearch?: boolean | ShowSearchType;
    changeOnSelect?: boolean;
    fieldNames?: Partial<FieldNames>;
    inputValue?: string;
    notFoundContent?: string;
    locale?: string;
    prefixCls?: string;
}

export interface UseOptionsReturn {
    fieldNames: FilledFieldNames;
    flattenOptions: CascaderOption[][];
    generateFilteredOptions: () => CascaderOption[];
}

/**
 * useOptions Hook
 *
 * @param props - UseOptionsProps
 * @returns UseOptionsReturn
 */
export const useOptions = (props: UseOptionsProps): UseOptionsReturn => {
    const {
        options,
        showSearch,
        changeOnSelect,
        fieldNames: propsFieldNames,
        inputValue = '',
        notFoundContent,
        locale,
        prefixCls,
    } = props;

    /**
     * getFilledFieldNames - 获取完整的字段名映射
     * 支持用户自定义字段名,未定义的使用默认值
     */
    const fieldNames = useMemo<FilledFieldNames>(() => {
        const customFieldNames = propsFieldNames || {};
        return {
            children: customFieldNames.children || 'children',
            label: customFieldNames.label || 'label',
            value: customFieldNames.value || 'value',
            ...(customFieldNames.fieldid && { fieldid: customFieldNames.fieldid }),
            ...(customFieldNames.id && { id: customFieldNames.id }),
        };
    }, [propsFieldNames]);

    /**
     * flattenTree - 递归展平树形结构
     *
     * @param data - 树形数据
     * @param ancestor - 祖先路径
     * @returns 展平后的路径数组
     */
    const flattenTree = useCallback(
        (data: CascaderOption[], ancestor: CascaderOption[] = []): CascaderOption[][] => {
            let result: CascaderOption[][] = [];
            const childrenName = fieldNames.children;

            data.forEach((option) => {
                const path = ancestor.concat(option);

                // changeOnSelect=true: 所有节点都可选
                // changeOnSelect=false: 只有叶子节点可选
                if (changeOnSelect || !option[childrenName] || !option[childrenName].length) {
                    result.push(path);
                }

                // 递归处理子节点
                if (option[childrenName] && option[childrenName].length > 0) {
                    result = result.concat(flattenTree(option[childrenName], path));
                }
            });

            return result;
        },
        [fieldNames, changeOnSelect]
    );

    /**
     * flattenOptions - 展平后的 options
     * 仅在 showSearch=true 时计算,使用 useMemo 缓存
     */
    const flattenOptions = useMemo(() => {
        if (!showSearch || !options || options.length === 0) {
            return [];
        }
        return flattenTree(options);
    }, [showSearch, options, flattenTree]);

    /**
     * getEmptyNode - 获取空节点(无匹配结果时显示)
     */
    const getEmptyNode = useCallback(
        (names: FieldNames, content: string) => {
            return {
                [names.value!]: 'ANT_CASCADER_NOT_FOUND',
                [names.label!]: content || <Empty locale={locale} />,
                disabled: true,
                isEmptyNode: true,
            };
        },
        [locale]
    );

    /**
     * defaultFilterOption - 默认过滤函数
     * 检查路径中是否有节点的 label 包含 inputValue
     */
    const defaultFilterOption = useCallback(
        (input: string, path: CascaderOption[]): boolean => {
            return path.some((option) => {
                const label =
                    typeof option[fieldNames.label] === 'string'
                        ? option[fieldNames.label]
                        : option?.title;
                return label && label.indexOf(input) > -1;
            });
        },
        [fieldNames]
    );

    /**
     * highlightKeyword - 高亮关键词
     */
    const highlightKeyword = useCallback(
        (str: string, keyword: string): React.ReactNode[] => {
            return str.split(keyword).map((node, index) =>
                index === 0
                    ? node
                    : [
                        <span className={`${prefixCls}-menu-item-keyword`} key="separator">
                            {keyword}
                        </span>,
                        node,
                    ]
            );
        },
        [prefixCls]
    );

    /**
     * defaultRenderFilteredOption - 默认渲染过滤选项
     * 渲染路径,并高亮关键词
     */
    const defaultRenderFilteredOption = useCallback(
        (input: string, path: CascaderOption[]): React.ReactNode => {
            return path.map((option, index) => {
                const label =
                    typeof option[fieldNames.label] === 'string'
                        ? option[fieldNames.label]
                        : option?.title || '';
                const node =
                    label.indexOf(input) > -1 ? highlightKeyword(label, input) : label;
                return index === 0 ? node : [' / ', node];
            });
        },
        [fieldNames, highlightKeyword]
    );

    /**
     * defaultSortFilteredOption - 默认排序函数
     * 按照匹配位置排序(越靠前越优先)
     */
    const defaultSortFilteredOption = useCallback(
        (a: CascaderOption[], b: CascaderOption[], input: string): number => {
            const callback = (elem: CascaderOption) => {
                const label =
                    typeof elem[fieldNames.label] === 'string'
                        ? elem[fieldNames.label]
                        : elem?.title || '';
                return label.indexOf(input) > -1;
            };
            return a.findIndex(callback) - b.findIndex(callback);
        },
        [fieldNames]
    );

    /**
     * generateFilteredOptions - 生成过滤后的选项
     *
     * @returns 过滤后的选项数组
     */
    const generateFilteredOptions = useCallback((): CascaderOption[] => {
        // inputValue 为空,返回空数组
        if (!inputValue || inputValue.trim() === '') {
            return [];
        }

        const showSearchConfig =
            typeof showSearch === 'object' ? showSearch : ({} as ShowSearchType);

        const {
            filter = defaultFilterOption,
            render = defaultRenderFilteredOption,
            sort = defaultSortFilteredOption,
            limit = 50,
        } = showSearchConfig;

        let filtered: CascaderOption[][] = [];

        // 根据 limit 限制结果数量
        if ((limit as number) > 0) {
            let matchCount = 0;
            flattenOptions.some((path) => {
                const match = filter(inputValue, path, fieldNames);
                if (match) {
                    filtered.push(path);
                    matchCount += 1;
                }
                return matchCount >= (limit as number);
            });
        } else {
            // limit <= 0 表示不限制
            filtered = flattenOptions.filter((path) =>
                filter(inputValue, path, fieldNames)
            );
        }

        // 排序
        filtered = filtered.sort((a, b) => sort(a, b, inputValue, fieldNames));

        // 有匹配结果
        if (filtered.length > 0) {
            // Fix issue: https://github.com/ant-design/ant-design/issues/26554
            const field =
                fieldNames.value === fieldNames.label
                    ? keepFilteredValueField
                    : fieldNames.value;

            return filtered.map((path) => ({
                __IS_FILTERED_OPTION: true,
                path,
                [field]: path.map((o) => o[fieldNames.value]),
                [fieldNames.label]: render(inputValue, path),
                disabled: path.some((o) => !!o.disabled),
                isEmptyNode: true,
            }));
        }

        // 无匹配结果,返回空节点
        return [getEmptyNode(fieldNames, notFoundContent as string)];
    }, [
        inputValue,
        showSearch,
        flattenOptions,
        fieldNames,
        defaultFilterOption,
        defaultRenderFilteredOption,
        defaultSortFilteredOption,
        getEmptyNode,
        notFoundContent,
    ]);

    return {
        fieldNames,
        flattenOptions,
        generateFilteredOptions,
    };
};
